import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { RefreshCw, Download, Save, History, ArrowUp, ArrowDown, ArrowUpDown, Pencil, LockKeyhole, X } from 'lucide-react';
import MultiSelect from '@/components/MultiSelect';
import DataTable from '@/components/DataTable';
import { dn } from '@/lib/channel-intel';
import { isExcludedFromTotals } from '@/lib/inventory-exclusions';
import {
  MONTHS, todayISO, toISO, mondayOf, addDays, parseLocalDate,
  weekBucketsForMonth, weekIndexForDate,
} from '@/lib/date-utils';
import { CHANNEL_OPTIONS, ALL_CHANNEL_VALUES } from '@/lib/allocation-config';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AllocationPlannerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Multi-select filters: [] = ALL (consistent across Channel / Brand / SKU)
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  // Date range — primary selector for snapshot lookup. Strictly 7-day window.
  // Default: this week's Monday → Sunday.
  const initialMonday = mondayOf(new Date());
  const [startDate, setStartDate] = useState(toISO(initialMonday));
  const [endDate, setEndDate] = useState(toISO(addDays(initialMonday, 6)));
  const [historicalSnapshot, setHistoricalSnapshot] = useState(null);
  const [snapshotsList, setSnapshotsList] = useState([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);

  // ============ Editor (Ankit) override flow ============
  // `editorUnlocked` is session-scoped — persisting across page reloads inside
  // the same browser tab so Ankit doesn't have to retype the password every
  // time the route mounts. Also kept in-memory so we can send the same
  // password back as X-Editor-Password on PUT calls.
  const [editorUnlocked, setEditorUnlocked] = useState(
    () => sessionStorage.getItem('editor_unlocked') === '1'
  );
  const [editorPwInMem, setEditorPwInMem] = useState(
    () => sessionStorage.getItem('editor_pw') || ''
  );
  const [editMode, setEditMode] = useState(false);
  // `editedSnapshot` holds the in-progress edits while in editMode. Shape
  // mirrors `historicalSnapshot`: { data: [...channelRows], skuData: [...skuRows] }.
  const [editedSnapshot, setEditedSnapshot] = useState(null);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPwInput, setUnlockPwInput] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);

  // Resolve "effective" channel filter: [] means ALL
  const effectiveChannels = selectedChannels.length === 0 ? ALL_CHANNEL_VALUES : selectedChannels;

  useEffect(() => {
    const token = localStorage.getItem('radar_session_token');
    if (!token) {
      navigate('/');
      return;
    }

    fetchAllocationData();
  }, [navigate]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedSkus, selectedBrands]);

  // Load snapshots index on mount
  useEffect(() => {
    fetchSnapshotsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load specific snapshot when date range changes
  useEffect(() => {
    loadSelectedSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('radar_session_token')}` });

  const fetchSnapshotsList = async () => {
    try {
      const res = await axios.get(`${API}/allocation/snapshots`, { headers: authHeader() });
      setSnapshotsList(res.data.snapshots || []);
    } catch (e) {
      // Silently ignore — list is best-effort metadata
    }
  };

  // Resolve a (month, week) key from the start date (always Monday-of-week).
  // We anchor every saved snapshot to the Monday of the week the user picked.
  const snapshotKeyFromStart = () => {
    const start = parseLocalDate(startDate);
    if (!start) return null;
    const monday = mondayOf(start);
    const monthName = MONTHS[monday.getMonth()];
    const buckets = weekBucketsForMonth(monday.getMonth(), monday.getFullYear());
    const idx = buckets.findIndex(
      b => b.start.getFullYear() === monday.getFullYear()
        && b.start.getMonth() === monday.getMonth()
        && b.start.getDate() === monday.getDate()
    );
    const week = idx >= 0 ? buckets[idx].key : 'Week 1';
    return { month: monthName, week, monday };
  };

  const loadSelectedSnapshot = async () => {
    const key = snapshotKeyFromStart();
    if (!key) { setHistoricalSnapshot(null); return; }
    try {
      const res = await axios.get(
        `${API}/allocation/snapshots/${encodeURIComponent(key.month)}/${encodeURIComponent(key.week)}`,
        { headers: authHeader() }
      );
      setHistoricalSnapshot(res.data);
    } catch (e) {
      // 404 = no snapshot for this week
      setHistoricalSnapshot(null);
    }
  };

  const exitHistoricalMode = () => {
    setHistoricalSnapshot(null);
    // Jump back to the current week
    const monday = mondayOf(new Date());
    setStartDate(toISO(monday));
    setEndDate(toISO(addDays(monday, 6)));
  };

  const saveAllocationSnapshot = async (allowOverwrite = false) => {
    if (!channelSummary || channelSummary.length === 0) {
      toast.error('No data to save');
      return;
    }
    const key = snapshotKeyFromStart();
    if (!key) { toast.error('Pick a valid date range first'); return; }

    setSavingSnapshot(true);
    try {
      // Build a lean SKU-level snapshot for the past-week view.
      // Saved against the live `enrichedData` and current Brand/SKU filters.
      const skuData = enrichedData.map(r => ({
        ASIN: r.ASIN || '',
        SKU: r.SKU || '',
        BRAND: r.BRAND || '',
        Total_Available: r['Total Available'] || '',
        Total_DRR: r['Total DRR'] || '',
        allocatableStock: r.allocatableStock || 0,
        offlineFinalRequirement: r.offlineFinalRequirement || 0,
        rawAllocatable: r.rawAllocatable || 0,
        isStockDeficit: !!r.isStockDeficit,
        isZeroDRR: !!r.isZeroDRR,
        allocations: r.allocations || {},
      }));

      const payload = {
        month: key.month,
        week: key.week,
        date: toISO(key.monday),
        data: channelSummary.map(r => ({
          channel: r.channel,
          allocated: r.allocated,
          salesShare: Number(r.salesSharePct.toFixed(2)),
        })),
        skuData,
        overwrite: allowOverwrite,
        safetyDays: null,
        totalAllocated: channelSummary.reduce((s, r) => s + r.allocated, 0),
      };
      const res = await axios.post(`${API}/allocation/snapshots`, payload, { headers: authHeader() });
      toast.success(res.data.replaced ? `Replaced ${key.month} / ${key.week}` : `Saved ${key.month} / ${key.week}`);
      await fetchSnapshotsList();
      await loadSelectedSnapshot();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`Snapshot already exists for ${key.month} / ${key.week}. Overwrite?`)) {
          return saveAllocationSnapshot(true);
        }
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save snapshot');
      }
    } finally {
      setSavingSnapshot(false);
    }
  };

  // ============ Editor flow handlers ============
  // Two-step: (1) unlock with editor password — kept in sessionStorage so it
  // persists across reloads in the same tab; (2) when entering edit mode we
  // clone the snapshot into `editedSnapshot` so cancelling reverts cleanly.
  const handleUnlock = async () => {
    if (!unlockPwInput.trim()) return;
    setUnlocking(true);
    try {
      await axios.post(
        `${API}/auth/editor-unlock`,
        { password: unlockPwInput },
        { headers: authHeader() }
      );
      sessionStorage.setItem('editor_unlocked', '1');
      sessionStorage.setItem('editor_pw', unlockPwInput);
      setEditorUnlocked(true);
      setEditorPwInMem(unlockPwInput);
      setUnlockDialogOpen(false);
      // Auto-enter edit mode once unlocked (only meaningful if a snapshot is loaded).
      if (historicalSnapshot) {
        setEditedSnapshot(JSON.parse(JSON.stringify(historicalSnapshot)));
        setEditMode(true);
      }
      setUnlockPwInput('');
      toast.success('Editor mode unlocked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid editor password');
    } finally {
      setUnlocking(false);
    }
  };

  const handleEnterEditMode = () => {
    if (!historicalSnapshot) {
      toast.error('Load a saved snapshot first');
      return;
    }
    if (!editorUnlocked) {
      setUnlockDialogOpen(true);
      return;
    }
    setEditedSnapshot(JSON.parse(JSON.stringify(historicalSnapshot)));
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditedSnapshot(null);
    setEditMode(false);
  };

  // Update a single SKU row's field (allocatableStock | offlineFinalRequirement
  // | allocations[<channelDrrKey>]). Always works on the cloned `editedSnapshot`.
  const updateEditedSku = (skuKey, field, value) => {
    setEditedSnapshot(prev => {
      if (!prev) return prev;
      const next = { ...prev, skuData: prev.skuData.map(r => {
        const rowKey = `${r.ASIN || 'no-asin'}-${r.SKU || ''}`;
        if (rowKey !== skuKey) return r;
        if (field.startsWith('alloc:')) {
          const chKey = field.slice(6);
          return { ...r, allocations: { ...(r.allocations || {}), [chKey]: value } };
        }
        return { ...r, [field]: value };
      })};
      return next;
    });
  };

  // Update a channel-summary row's "allocated" value (used for the compact
  // Channel-wise Allocation card during edit mode).
  const updateEditedChannelRow = (channelLabel, value) => {
    setEditedSnapshot(prev => {
      if (!prev) return prev;
      const next = { ...prev, data: prev.data.map(d => (
        d.channel === channelLabel ? { ...d, allocated: value } : d
      ))};
      return next;
    });
  };

  const handleSaveEdits = async () => {
    if (!editedSnapshot) return;
    const key = snapshotKeyFromStart();
    if (!key) { toast.error('Cannot resolve target snapshot'); return; }
    setSavingEdits(true);
    try {
      const payload = {
        data: (editedSnapshot.data || []).map(d => ({
          channel: d.channel,
          allocated: Math.round(Number(d.allocated) || 0),
          salesShare: Number(d.salesShare || 0),
        })),
        skuData: editedSnapshot.skuData || [],
        totalAllocated: (editedSnapshot.data || []).reduce(
          (s, d) => s + (Math.round(Number(d.allocated) || 0)), 0
        ),
      };
      const res = await axios.put(
        `${API}/allocation/snapshots/${encodeURIComponent(key.month)}/${encodeURIComponent(key.week)}`,
        payload,
        { headers: { ...authHeader(), 'X-Editor-Password': editorPwInMem } }
      );
      setHistoricalSnapshot(res.data.snapshot);
      setEditedSnapshot(null);
      setEditMode(false);
      toast.success('Saved updated allocation');
      await fetchSnapshotsList();
    } catch (e) {
      if (e.response?.status === 401) {
        // Editor password may have rotated server-side — force re-unlock.
        sessionStorage.removeItem('editor_unlocked');
        sessionStorage.removeItem('editor_pw');
        setEditorUnlocked(false);
        setEditorPwInMem('');
        toast.error('Editor unlock expired — please re-enter the password');
        setUnlockDialogOpen(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save edits');
      }
    } finally {
      setSavingEdits(false);
    }
  };

  const fetchAllocationData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('radar_session_token');
      const response = await axios.get(`${API}/sheets/allocation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = response.data.rows;
      if (rows.length > 1) {
        const headers = rows[0];
        const dataRows = rows.slice(1).map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });
          return obj;
        });

        // ============ Build SKU → Brand map from allocation_source (Col T) ============
        // Case-insensitive trimmed SKU key. Warn for any SKU missing a brand.
        const brandMap = {};
        const norm = (s) => (s || '').toString().trim().toLowerCase();
        dataRows.forEach(r => {
          const sku = norm(r.SKU);
          if (!sku) return;
          const brand = (r.Brand || r.BRAND || '').toString().trim();
          if (!brand) {
            console.warn(`[allocation] SKU "${r.SKU}" missing Brand in allocation_source (Col T)`);
          } else {
            brandMap[sku] = brand;
          }
        });

        // Attach BRAND to every row via the map (falls back to "Unknown" when unmapped).
        const enriched = dataRows.map(r => {
          const sku = norm(r.SKU);
          const brand = brandMap[sku];
          if (!brand && sku) {
            console.warn(`[allocation] SKU "${r.SKU}" not found in brand map — using "Unknown"`);
          }
          return { ...r, BRAND: brand || 'Unknown' };
        });

        setData(enriched);
        toast.success('Allocation data loaded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch allocation data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // SKU filter + Brand filter narrow the row set. Channel filter restricts which channel
    // allocation columns are shown/aggregated — applied separately at render/aggregation time.
    let filtered = data;
    if (selectedSkus.length > 0) {
      const set = new Set(selectedSkus);
      filtered = filtered.filter(row => set.has(row.SKU));
    }
    if (selectedBrands.length > 0) {
      const set = new Set(selectedBrands);
      filtered = filtered.filter(row => set.has(row.BRAND));
    }
    setFilteredData(filtered);
  };

  const parseNumber = (value) => {
    if (!value) return 0;
    const cleaned = value.toString().trim().replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const formatIndianNumber = (value) => {
    return Math.round(value).toLocaleString('en-IN');
  };

  const getCurrentMonthProgress = () => {
    const now = new Date();
    const dayOfMonth = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return (dayOfMonth / daysInMonth) * 100;
  };

  const monthProgress = getCurrentMonthProgress();

  // Round all final allocations to nearest 10
  const roundTo10 = (n) => Math.round((n || 0) / 10) * 10;

  const calculateAllocations = (row) => {
    const totalDRR = parseNumber(row['Total DRR']);
    // Per-SKU Safety Days comes from Google Sheet col "Safety Stock (Days)".
    // Fallback to 5 if missing / non-numeric.
    const sheetSafetyDays = parseNumber(row['Safety Stock (Days)']);
    const safetyStockDays = sheetSafetyDays > 0 ? sheetSafetyDays : 5;
    const allocationBaseStock = parseNumber(row['Allocation Base Stock']);
    const offlineProjected = parseNumber(row['Offline Projected Consumed']);
    const offlineConsumedPct = parseNumber(row['Offline Consumed %']);
    const b2bDRR = parseNumber(row['B2B DRR']);

    // Per-channel DRRs needed for B2C reservation + online split
    const azDRR     = parseNumber(row['AZ DRR']);
    const fkDRR     = parseNumber(row['FK DRR']);
    const meeshoDRR = parseNumber(row['Meesho DRR']);
    const winDRR    = parseNumber(row['W.in DRR']);

    // === B2C Order Stock — 7-day reserve ===
    // AZ 10%·7d, FK 10%·7d, Meesho 100%·7d, W.in 50%·7d; everything else 0.
    const b2cAZ     = azDRR > 0     ? azDRR     * 7 * 0.10 : 0;
    const b2cFK     = fkDRR > 0     ? fkDRR     * 7 * 0.10 : 0;
    const b2cMeesho = meeshoDRR > 0 ? meeshoDRR * 7 * 1.00 : 0;
    const b2cWin    = winDRR > 0    ? winDRR    * 7 * 0.50 : 0;
    const b2cTotal  = b2cAZ + b2cFK + b2cMeesho + b2cWin;

    // === Allocatable (FINAL, canonical) ===========================================
    //   Allocatable = Base Stock − Safety Stock − B2C Stock
    //   • Zero DRR or negative raw → effective Allocatable = 0 (excluded from
    //     allocation math & totals; channel outputs all 0 for this SKU).
    //   • Single source of truth used by SKU table, channel summary & totals row.
    const safetyStockUnits = totalDRR * safetyStockDays;
    const originalAllocatableStock = allocationBaseStock - safetyStockUnits;
    const rawAllocatable = originalAllocatableStock - b2cTotal;
    const overReserved = b2cTotal > Math.max(0, originalAllocatableStock);

    const isZeroDRR = totalDRR === 0;
    const isStockDeficit = rawAllocatable < 0;
    // Effective (displayed & allocated) Allocatable — zero when excluded.
    // Rounded to integer at SKU level so per-SKU sum == allocatableStock exactly,
    // and sum-across-SKUs display matches column-wise display with no off-by-one drift.
    const allocatableStock = (isZeroDRR || isStockDeficit) ? 0 : Math.round(rawAllocatable);

    // Allocation slots — default all zero; populated only when SKU is eligible.
    const allocationKeys = ['AZ DRR', 'FK DRR', 'Blinkit DRR', 'W.in DRR', 'Zepto DRR', 'Meesho DRR', 'FK Mins DRR', 'B2B DRR'];
    const allocations = {};
    allocationKeys.forEach(k => { allocations[k] = 0; });
    let offlineFinalRequirement = 0;
    let offlineEligible = false;
    let offlineOverconsumed = offlineProjected < 0;

    if (allocatableStock > 0) {
      // --- Offline (B2B) allocation ---
      const b2bProportional = totalDRR > 0 ? (b2bDRR / totalDRR) * allocatableStock : 0;
      let offlineRaw;
      if (offlineOverconsumed) {
        offlineRaw = b2bProportional;
      } else {
        offlineRaw = Math.max(b2bProportional, offlineProjected);
        offlineEligible = true;
      }
      // Cap: offline never exceeds Allocatable
      offlineRaw = Math.min(allocatableStock, Math.max(0, offlineRaw));

      const remaining = Math.max(0, allocatableStock - offlineRaw);

      // --- Online split (Meesho forced 0, so excluded from denominator) ---
      const splittable = ['AZ DRR', 'FK DRR', 'Blinkit DRR', 'W.in DRR', 'Zepto DRR', 'FK Mins DRR'];
      const onlineTotalDRR = splittable.reduce((s, ch) => s + parseNumber(row[ch]), 0);

      const rawChannels = {};
      splittable.forEach(ch => {
        rawChannels[ch] = (remaining > 0 && onlineTotalDRR > 0)
          ? (parseNumber(row[ch]) / onlineTotalDRR) * remaining
          : 0;
      });

      // --- Round each allocation to nearest 10 ---
      const roundedChannels = {};
      splittable.forEach(ch => { roundedChannels[ch] = roundTo10(Math.max(0, rawChannels[ch])); });
      let offlineRounded = roundTo10(offlineRaw);

      // --- Rounding-diff redistribution ---
      // Ensure: Σ(offline + channel allocations) == Allocatable (per SKU).
      // Push the residual onto the largest allocation slot (tie-break by DRR).
      const sumRounded = offlineRounded + splittable.reduce((s, ch) => s + roundedChannels[ch], 0);
      const diff = allocatableStock - sumRounded;
      if (diff !== 0) {
        const slots = [
          { key: 'offline', value: offlineRounded, drr: b2bDRR },
          ...splittable.map(ch => ({ key: ch, value: roundedChannels[ch], drr: parseNumber(row[ch]) })),
        ];
        // Prefer largest allocated value; if all zero, fall back to largest DRR.
        slots.sort((a, b) => (b.value - a.value) || (b.drr - a.drr));
        const target = slots[0];
        const adjusted = Math.max(0, target.value + diff);
        if (target.key === 'offline') {
          offlineRounded = adjusted;
        } else {
          roundedChannels[target.key] = adjusted;
        }
      }

      // Commit
      offlineFinalRequirement = offlineRounded;
      splittable.forEach(ch => { allocations[ch] = roundedChannels[ch]; });
      allocations['Meesho DRR'] = 0; // explicit: always 0
      allocations['B2B DRR']   = 0; // offline is tracked separately
    }

    // Total allocated — by construction equals Allocatable when SKU is eligible.
    const totalAllocated =
      offlineFinalRequirement +
      ['AZ DRR', 'FK DRR', 'Blinkit DRR', 'W.in DRR', 'Zepto DRR', 'FK Mins DRR']
        .reduce((s, ch) => s + (allocations[ch] || 0), 0);
    const unallocatedBalance = allocatableStock - totalAllocated;

    // Remaining stock (legacy field — kept for compatibility with any UI reads)
    const remainingStock = Math.max(0, allocatableStock - offlineFinalRequirement);

    // ------------- Status labels -------------
    let allocationStatus = 'Ready for Allocation';
    let allocationColor = 'text-[#15803D]';
    let allocationBg = 'bg-[#DCFCE7]';
    if (isStockDeficit) {
      allocationStatus = 'Stock Deficit';
      allocationColor = 'text-[#B91C1C]';
      allocationBg = 'bg-[#FEE2E2]';
    } else if (overReserved) {
      allocationStatus = 'Over-reserved';
      allocationColor = 'text-[#B91C1C]';
      allocationBg = 'bg-[#FEE2E2]';
    } else if (isZeroDRR) {
      allocationStatus = 'Zero DRR';
      allocationColor = 'text-[#64748B]';
      allocationBg = 'bg-[#F1F5F9]';
    }

    let offlineStatus = 'No Offline Requirement';
    let offlineColor = 'text-[#64748B]';
    let offlineBg = 'bg-[#F1F5F9]';
    if (offlineOverconsumed) {
      offlineStatus = 'Overconsumed';
      offlineColor = 'text-[#B91C1C]';
      offlineBg = 'bg-[#FEE2E2]';
    } else if (offlineConsumedPct > 0) {
      const progress = getCurrentMonthProgress();
      const pacingDiff = offlineConsumedPct - progress;
      if (pacingDiff > 10) {
        offlineStatus = 'Over Pace';
        offlineColor = 'text-[#B91C1C]';
        offlineBg = 'bg-[#FEE2E2]';
      } else if (pacingDiff < -10) {
        offlineStatus = 'Under Pace';
        offlineColor = 'text-[#C2410C]';
        offlineBg = 'bg-[#FFEDD5]';
      } else if (offlineFinalRequirement > 0) {
        offlineStatus = 'On Track';
        offlineColor = 'text-[#15803D]';
        offlineBg = 'bg-[#DCFCE7]';
      }
    }

    return {
      safetyStockUnits,
      originalAllocatableStock,
      allocatableStock,      // EFFECTIVE (used everywhere)
      rawAllocatable,        // raw signed (diagnostic)
      b2cTotal,
      b2cAZ, b2cFK, b2cMeesho, b2cWin,
      overReserved,
      offlineFinalRequirement,
      remainingStock,
      isStockDeficit,
      isZeroDRR,
      allocationStatus,
      allocationColor,
      allocationBg,
      allocations,
      totalAllocated,
      unallocatedBalance,
      offlineEligible,
      offlineOverconsumed,
      offlineStatus,
      offlineColor,
      offlineBg,
    };
  };

  const enrichedData = filteredData.map(row => ({
    ...row,
    ...calculateAllocations(row)
  }));

  // Distinct, alphabetised SKU options for the multi-select
  const skuOptions = (() => {
    const uniq = [...new Set(data.map(r => r.SKU).filter(Boolean))].sort();
    return uniq.map(s => ({ value: s, label: s }));
  })();

  // Distinct, alphabetised brand options
  const brandOptions = (() => {
    const uniq = [...new Set(data.map(r => r.BRAND).filter(Boolean))].sort();
    return uniq.map(b => ({ value: b, label: b }));
  })();

  // ============ Channel-wise allocation summary (table) ============
  // Respects both filters: SKU filter narrows enrichedData; channel filter restricts which
  // channels are included. Sales Share % = (channel DRR / total DRR) × 100 across visible channels.
  const channelSummary = (() => {
    const visible = CHANNEL_OPTIONS.filter(opt => effectiveChannels.includes(opt.value));
    // Compute totals first
    const rows = visible.map(opt => {
      const allocated = opt.value === 'B2B DRR'
        ? enrichedData.reduce((s, r) => s + (r.offlineFinalRequirement || 0), 0)
        : enrichedData.reduce((s, r) => s + (r.allocations?.[opt.value] || 0), 0);
      const drr = enrichedData.reduce((s, r) => s + parseNumber(r[opt.value]), 0);
      return { channel: opt.label, allocated: Math.round(allocated), drr };
    });
    const totalDRR = rows.reduce((s, r) => s + r.drr, 0);
    return rows.map(r => ({
      ...r,
      salesSharePct: totalDRR > 0 ? (r.drr / totalDRR) * 100 : 0,
    }));
  })();

  // Convenience: does the currently selected (date range) bucket have a snapshot?
  const _key = snapshotKeyFromStart();
  const selectedMonthName = _key?.month;
  const selectedWeekKey = _key?.week;
  const snapshotExists = snapshotsList.some(s => s.month === selectedMonthName && s.week === selectedWeekKey);
  const isHistorical = !!historicalSnapshot;

  // Date-range classification driving the SKU table data source.
  //   'live'   → range includes today → recompute from live sheet data
  //   'past'   → range entirely before today → load saved snapshot SKU rows
  //   'future' → range entirely after today → no data
  const dateRangeMode = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const s = parseLocalDate(startDate); if (s) s.setHours(0, 0, 0, 0);
    const e = parseLocalDate(endDate);   if (e) e.setHours(0, 0, 0, 0);
    if (!s || !e) return 'live';
    if (today >= s && today <= e) return 'live';
    if (e < today) return 'past';
    return 'future';
  })();

  // Channel-wise display:
  //   live   → recompute live, apply Channel filter (already handled inside channelSummary)
  //   past   → use saved snapshot (filtered by Channel); empty if no snapshot
  //   future → empty (no data)
  //   editMode (overlays past) → use `editedSnapshot.data` so in-progress edits
  //   are reflected immediately in the Channel-wise card and totals.
  const displayChannelSummary = (() => {
    if (dateRangeMode === 'future') return [];
    if (dateRangeMode === 'past') {
      const source = (editMode && editedSnapshot) ? editedSnapshot : historicalSnapshot;
      if (!source) return [];
      return source.data
        .filter(d => {
          if (selectedChannels.length === 0) return true;
          const opt = CHANNEL_OPTIONS.find(o => o.label === d.channel);
          return opt ? selectedChannels.includes(opt.value) : true;
        })
        .map(d => ({
          channel: d.channel,
          allocated: Math.round(d.allocated || 0),
          salesSharePct: Number(d.salesShare || 0),
        }));
    }
    // live
    return channelSummary;
  })();

  // SKU-level display rows. Always rendered (never hidden).
  //   live   → enrichedData (calculated)
  //   past   → snapshot.skuData (fall back to empty if older snapshot has no SKU data)
  //   future → []
  //   editMode (overlays past) → editedSnapshot.skuData so edits paint live.
  const displaySkuRows = (() => {
    if (dateRangeMode === 'future') return [];
    if (dateRangeMode === 'past') {
      const source = (editMode && editedSnapshot) ? editedSnapshot : historicalSnapshot;
      const arr = source?.skuData || [];
      // Apply Brand/SKU filters on saved rows (saved BRAND lives at row.BRAND).
      return arr.filter(r => {
        if (selectedBrands.length > 0 && !selectedBrands.includes(r.BRAND)) return false;
        if (selectedSkus.length > 0 && !selectedSkus.includes(r.SKU)) return false;
        return true;
      });
    }
    return enrichedData;
  })();

  // Empty-state message for SKU table by mode.
  const skuEmptyMessage = (() => {
    if (dateRangeMode === 'future') return 'No data available for selected period';
    if (dateRangeMode === 'past' && !historicalSnapshot) return 'No allocation saved for this date range';
    if (dateRangeMode === 'past' && (!historicalSnapshot.skuData || historicalSnapshot.skuData.length === 0)) {
      return 'Saved snapshot has no SKU-level data';
    }
    return 'No SKUs match the current filters';
  })();

  // Sorting state for Channel-wise Allocation Summary (separate from main DataTable)
  const [channelSortKey, setChannelSortKey] = useState(null);
  const [channelSortDir, setChannelSortDir] = useState('asc');
  const toggleChannelSort = (key) => {
    if (channelSortKey !== key) { setChannelSortKey(key); setChannelSortDir('asc'); return; }
    if (channelSortDir === 'asc') { setChannelSortDir('desc'); return; }
    setChannelSortKey(null); setChannelSortDir('asc');
  };
  const sortedChannelSummary = (() => {
    if (!channelSortKey) return displayChannelSummary;
    const copy = [...displayChannelSummary];
    copy.sort((a, b) => {
      const av = a[channelSortKey]; const bv = b[channelSortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return channelSortDir === 'asc' ? av - bv : bv - av;
      }
      const sa = (av ?? '').toString().toLowerCase();
      const sb = (bv ?? '').toString().toLowerCase();
      if (sa < sb) return channelSortDir === 'asc' ? -1 : 1;
      if (sa > sb) return channelSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  })();

  const kpis = {
    totalAvailable: filteredData.reduce((sum, row) => sum + parseNumber(row['Total Available']), 0),
    totalRequested: filteredData.reduce((sum, row) => sum + parseNumber(row['Current Week Requested']), 0),
    // Exclude parent SKUs (Steel Shakers, Glass Bottle Shaker) — children counted separately.
    allocationBaseStock: filteredData
      .filter(row => !isExcludedFromTotals(row.SKU))
      .reduce((sum, row) => sum + parseNumber(row['Allocation Base Stock']), 0),
    totalSafetyStock: enrichedData.reduce((sum, row) => sum + row.safetyStockUnits, 0),
    totalAllocatable: enrichedData.reduce((sum, row) => sum + Math.max(0, row.allocatableStock), 0),
    totalDRR: filteredData.reduce((sum, row) => sum + parseNumber(row['Total DRR']), 0),
    deficitSKUs: enrichedData.filter(row => row.isStockDeficit).length,
    zeroDRRSKUs: enrichedData.filter(row => row.isZeroDRR).length,
    offlineEligible: enrichedData.reduce((sum, row) => sum + row.offlineFinalRequirement, 0),
    offlineOverconsumed: enrichedData.filter(row => row.offlineOverconsumed).length,
    totalB2C: enrichedData.reduce((sum, row) => sum + row.b2cTotal, 0),
    overReservedSKUs: enrichedData.filter(row => row.overReserved).length,
    avgDOH: filteredData.length > 0 ? filteredData.reduce((sum, row) => sum + parseNumber(row['Total DOH']), 0) / filteredData.length : 0
  };

  // ============ Excel Export ============
  // Works for both LIVE (computed `enrichedData`) and PAST (`historicalSnapshot.skuData`).
  // The download button is enabled whenever `displaySkuRows` has rows.
  const handleDownloadExcel = () => {
    const isPast = dateRangeMode === 'past' && historicalSnapshot;
    const sourceRows = isPast ? (historicalSnapshot.skuData || []) : enrichedData;
    if (!sourceRows || sourceRows.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Build dynamic columns matching the visible table (B2B/Offline + per-channel toggles)
    const includeOffline = effectiveChannels.includes('B2B DRR');
    const channelCols = CHANNEL_OPTIONS.filter(
      opt => opt.value !== 'B2B DRR' && effectiveChannels.includes(opt.value)
    );

    const headers = ['ASIN', 'SKU', 'Brand', 'Allocatable'];
    if (includeOffline) headers.push('Offline');
    channelCols.forEach(opt => headers.push(opt.label));

    const rows = sourceRows.map(row => {
      // Past snapshots store `Total_Available` not `Total Available`, and don't have
      // `b2cTotal`. We normalise field access here.
      const r = [
        row.ASIN || '',
        row.SKU || '',
        row.BRAND || '',
        Math.round(row.allocatableStock || 0),
      ];
      if (includeOffline) r.push(Math.round(row.offlineFinalRequirement || 0));
      channelCols.forEach(opt => r.push(Math.round(row.allocations?.[opt.value] || 0)));
      return r;
    });

    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Numeric formatting: first 3 columns (ASIN, SKU, Brand) are text; rest numeric.
    const numericStartCol = 3;
    for (let R = 1; R < aoa.length; R++) {
      for (let C = numericStartCol; C < headers.length; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[cellRef]) {
          ws[cellRef].t = 'n';
          ws[cellRef].z = '0';
        }
      }
    }

    ws['!cols'] = headers.map((h, i) => ({
      wch: Math.max(
        h.length + 2,
        ...rows.map(r => (r[i] === undefined || r[i] === null ? 0 : r[i].toString().length + 2))
      ),
    }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    ws['!views'] = [{ state: 'frozen', ySplit: 1 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isPast ? 'Saved Allocation' : 'Allocation Data');

    // Add a Channel Summary sheet for the same export — useful for ops to see
    // both views in one file.
    const summaryAoa = [['Channel', 'Allocated Units', 'Sales Share %']];
    displayChannelSummary.forEach(s => {
      summaryAoa.push([s.channel, Math.round(s.allocated || 0), Number(s.salesSharePct || 0)]);
    });
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa);
    wsSummary['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Channel Summary');

    const tag = isPast
      ? `Snapshot_${historicalSnapshot.month}_${historicalSnapshot.week.replace(/\s+/g, '')}`.replace(/'/g, '')
      : `Live_${new Date().toISOString().slice(0, 10)}`;
    XLSX.writeFile(wb, `Stock_Allocation_${tag}.xlsx`);
    toast.success(`Downloaded ${rows.length} SKUs${isPast ? ' (saved snapshot)' : ''}`);
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">Stock Allocation Planner</h1>
          <Button onClick={fetchAllocationData} disabled={loading}>
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-4 md:p-8 space-y-6">
        {data.length > 0 && (
          <>
            <section>
              {/* Row 1: Filters — All reset | Channel | Brand | SKU */}
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr] gap-3 mb-4 items-end" data-testid="filters-row">
                <div className="flex flex-col">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block invisible">&nbsp;</Label>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="filters-all-reset"
                    onClick={() => { setSelectedChannels([]); setSelectedBrands([]); setSelectedSkus([]); }}
                    className="h-9"
                    title="Clear all filters"
                  >
                    All
                  </Button>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Channel Filter</Label>
                  <MultiSelect
                    testId="channel-filter"
                    options={CHANNEL_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                    selected={selectedChannels}
                    onChange={setSelectedChannels}
                    placeholder="All Channels"
                    emptyLabel="All Channels"
                    searchable
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Brand Filter</Label>
                  <MultiSelect
                    testId="brand-filter"
                    options={brandOptions}
                    selected={selectedBrands}
                    onChange={setSelectedBrands}
                    placeholder="All Brands"
                    emptyLabel="All Brands"
                    searchable
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">SKU Filter</Label>
                  <MultiSelect
                    testId="sku-filter"
                    options={skuOptions}
                    selected={selectedSkus}
                    onChange={setSelectedSkus}
                    placeholder="All SKUs"
                    emptyLabel="All SKUs"
                    searchable
                  />
                </div>
              </div>

              {/* Row 2: Date Range — strictly 7 days, primary selector */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 items-end" data-testid="date-range-row">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      setStartDate(val);
                      setEndDate(toISO(addDays(parseLocalDate(val), 6)));
                    }}
                    data-testid="start-date-input"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      const newEnd = parseLocalDate(val);
                      const cur = parseLocalDate(startDate);
                      const diff = Math.round((newEnd - cur) / 86400000);
                      if (diff !== 6) {
                        // Snap range back to a strict 7-day window: realign Start = End - 6 days.
                        toast.message('Date range locked to 7 days — adjusted Start to match.');
                        setStartDate(toISO(addDays(newEnd, -6)));
                      }
                      setEndDate(val);
                    }}
                    data-testid="end-date-input"
                    className="h-9"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Range is fixed at 7 days</p>
                </div>
              </div>

              {/* Row 3: Save Allocation button */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Button
                  onClick={() => saveAllocationSnapshot(false)}
                  disabled={savingSnapshot || editMode}
                  data-testid="save-allocation-button"
                  title={editMode ? 'Exit edit mode first' : 'Save snapshot for the selected date range'}
                >
                  <Save size={14} className="mr-2" />
                  {savingSnapshot ? 'Saving…' : 'Save Allocation'}
                </Button>
                {isHistorical && (
                  <Button
                    onClick={exitHistoricalMode}
                    variant="outline"
                    size="sm"
                    data-testid="exit-historical-button"
                    disabled={editMode}
                  >
                    This Week
                  </Button>
                )}
                {/* Edit Allocation — only meaningful when viewing a saved snapshot.
                    Gated by editor password unlock; once unlocked, the button
                    becomes "Save Updated Allocation" + "Cancel". */}
                {isHistorical && !editMode && (
                  <Button
                    onClick={handleEnterEditMode}
                    variant="outline"
                    size="sm"
                    data-testid="enter-edit-mode-button"
                    title={editorUnlocked ? 'Override saved allocation values' : 'Unlock with editor password'}
                  >
                    {editorUnlocked ? <Pencil size={14} className="mr-2" /> : <LockKeyhole size={14} className="mr-2" />}
                    Edit Allocation
                  </Button>
                )}
                {editMode && (
                  <>
                    <Button
                      onClick={handleSaveEdits}
                      size="sm"
                      disabled={savingEdits}
                      data-testid="save-updated-allocation-button"
                      className="bg-[#0F0F0F] text-white hover:bg-[#0F0F0F]/90"
                    >
                      <Save size={14} className="mr-2" />
                      {savingEdits ? 'Saving…' : 'Save Updated Allocation'}
                    </Button>
                    <Button
                      onClick={handleCancelEdit}
                      variant="outline"
                      size="sm"
                      data-testid="cancel-edit-button"
                    >
                      <X size={14} className="mr-2" />
                      Cancel
                    </Button>
                  </>
                )}
              </div>

              {isHistorical && (
                <div
                  className={`mb-4 px-4 py-2 rounded-md border flex items-center gap-2 ${editMode ? 'bg-[#FFEDD5] border-[#FB923C]' : 'bg-[#FEF9C3] border-[#FACC15]'}`}
                  data-testid="historical-banner"
                >
                  {editMode ? <Pencil size={16} className="text-[#C2410C]" /> : <History size={16} className="text-[#A16207]" />}
                  <span className={`text-xs font-medium ${editMode ? 'text-[#C2410C]' : 'text-[#A16207]'}`}>
                    {editMode
                      ? `Editing saved allocation · ${historicalSnapshot.date} (${historicalSnapshot.month} · ${historicalSnapshot.week}) — changes are unsaved`
                      : `Saved snapshot · ${historicalSnapshot.date} (${historicalSnapshot.month} · ${historicalSnapshot.week})`}
                  </span>
                </div>
              )}

              {/* Filter summary + Excel */}
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="text-xs text-muted-foreground" data-testid="filter-summary">
                  Showing <span className="font-semibold text-wv-ink">{displaySkuRows.length}</span> SKU{displaySkuRows.length === 1 ? '' : 's'}
                  {isHistorical && (
                    <> · <span className="font-semibold text-[#A16207]">from saved snapshot</span></>
                  )}
                  {selectedBrands.length > 0 && (
                    <> · <span className="font-semibold text-wv-ink">{selectedBrands.length}</span> brand{selectedBrands.length === 1 ? '' : 's'}</>
                  )}
                  {selectedChannels.length > 0 && (
                    <> · <span className="font-semibold text-wv-ink">{selectedChannels.length}</span> channel{selectedChannels.length === 1 ? '' : 's'}</>
                  )}
                </span>
                <Button
                  onClick={handleDownloadExcel}
                  variant="outline"
                  size="sm"
                  disabled={displaySkuRows.length === 0}
                  data-testid="download-excel-button"
                  className="shrink-0"
                >
                  <Download size={14} className="mr-2" />
                  {isHistorical ? 'Download Saved' : 'Download Excel'}
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Base Stock</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-medium">{formatIndianNumber(kpis.allocationBaseStock)}</p>
                  </CardContent>
                </Card>

                <Card className="border-border" data-testid="kpi-b2c-total">
                  <CardHeader className="pb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">B2C Order Stock</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-semibold text-[#7C3AED]">{formatIndianNumber(kpis.totalB2C)}</p>
                    {kpis.overReservedSKUs > 0 && (
                      <p className="text-[11px] text-[#B91C1C] mt-1">{kpis.overReservedSKUs} over-reserved</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Allocatable</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-medium text-primary">{formatIndianNumber(kpis.totalAllocatable)}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">After B2C reserve</p>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total DRR</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-medium">{formatIndianNumber(kpis.totalDRR)}</p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Channel-wise Allocation Summary (compact table, ~50% width) */}
            <section data-testid="alloc-summary-section" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-sm font-semibold text-wv-ink flex items-center gap-2">
                      Channel-wise Allocation
                      {isHistorical && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#FEF9C3] text-[#A16207]">
                          Historical
                        </span>
                      )}
                    </CardTitle>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {isHistorical
                        ? `Saved on ${historicalSnapshot.date} · ${historicalSnapshot.month} · ${historicalSnapshot.week}`
                        : `Date range: ${startDate} → ${endDate}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</p>
                    <p className="text-base font-semibold text-wv-ink" data-testid="alloc-summary-total">
                      {formatIndianNumber(sortedChannelSummary.reduce((s, r) => s + r.allocated, 0))}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pb-4">
                  {sortedChannelSummary.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground" data-testid="alloc-summary-empty">
                      No allocation available
                    </p>
                  ) : (
                    <div className="border border-border rounded-md overflow-hidden">
                      <div className="max-h-[280px] overflow-auto">
                        <table className="w-full text-sm table-fixed" data-testid="alloc-summary-table">
                          <colgroup>
                            <col className="w-[40%]" />
                            <col className="w-[30%]" />
                            <col className="w-[30%]" />
                          </colgroup>
                          <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur">
                            <tr>
                              {[
                                { key: 'channel', label: 'Channel', align: 'left' },
                                { key: 'allocated', label: 'Stock Allocated', align: 'right' },
                                { key: 'salesSharePct', label: 'Sales Share %', align: 'right' },
                              ].map(h => {
                                const isActive = channelSortKey === h.key;
                                const Icon = isActive ? (channelSortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                                return (
                                  <th
                                    key={h.key}
                                    onClick={() => toggleChannelSort(h.key)}
                                    data-testid={`channel-summary-sort-${h.key}`}
                                    className="p-2 text-[11px] font-semibold uppercase tracking-wider text-wv-ink border-b border-border cursor-pointer select-none hover:text-primary"
                                    style={{ textAlign: h.align }}
                                  >
                                    <span className={`inline-flex items-center gap-1 ${h.align === 'right' ? 'flex-row-reverse' : ''}`}>
                                      {h.label}
                                      <Icon size={11} className={isActive ? 'text-primary' : 'text-[#94A3B8]'} />
                                    </span>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedChannelSummary.map((r, idx) => (
                              <tr
                                key={r.channel}
                                className={`${idx % 2 === 1 ? 'bg-muted/20' : 'bg-card'} hover:bg-primary/5 transition-colors`}
                                data-testid={`alloc-summary-row-${r.channel.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <td className="text-left p-2 text-xs text-wv-body">{r.channel}</td>
                                <td className="text-right p-2 text-xs font-medium tabular-nums text-wv-ink">
                                  {editMode ? (
                                    <input
                                      type="number"
                                      defaultValue={Math.round(Number(r.allocated) || 0)}
                                      onBlur={(e) => updateEditedChannelRow(r.channel, Math.round(Number(e.target.value) || 0))}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      className="w-24 text-right tabular-nums px-1 py-0.5 border border-[#FB923C] rounded bg-[#FFF7ED] text-[12px] focus:outline-none focus:ring-1 focus:ring-[#FB823C]"
                                      data-testid={`edit-channel-allocated-${r.channel.toLowerCase().replace(/\s+/g, '-')}`}
                                    />
                                  ) : formatIndianNumber(r.allocated)}
                                </td>
                                <td className="text-right p-2 text-xs tabular-nums text-wv-body">
                                  {r.salesSharePct.toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-primary/5 border-t-2 border-border">
                              <td className="text-left p-2 text-xs font-semibold text-wv-ink">Total</td>
                              <td className="text-right p-2 text-xs font-semibold tabular-nums text-wv-ink">
                                {formatIndianNumber(sortedChannelSummary.reduce((s, r) => s + r.allocated, 0))}
                              </td>
                              <td className="text-right p-2 text-xs font-semibold tabular-nums text-wv-ink">
                                {sortedChannelSummary.reduce((s, r) => s + r.salesSharePct, 0).toFixed(1)}%
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                  {!isHistorical && snapshotExists && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Snapshot exists for {selectedMonthName} · {selectedWeekKey} — switch above to view it.
                    </p>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* SKU-level table — ALWAYS rendered. Source switches by date-range mode. */}
            <section data-testid="sku-table-section">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-sm font-semibold text-wv-ink">SKU-wise Allocation</h2>
                  <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="sku-table-mode">
                    {dateRangeMode === 'live'
                      ? 'Live data — calculated from current sheet'
                      : dateRangeMode === 'past'
                      ? historicalSnapshot
                        ? `Saved snapshot · ${historicalSnapshot.date} (${historicalSnapshot.month} · ${historicalSnapshot.week})`
                        : 'No saved snapshot for this range'
                      : 'Future range — no data'}
                  </p>
                </div>
              </div>
              <DataTable
                testId="alloc-sku-table"
                searchKeys={['ASIN', 'SKU', 'BRAND']}
                searchPlaceholder="Search SKU, ASIN, or brand…"
                emptyMessage={skuEmptyMessage}
                maxHeight="640px"
                defaultPageSize={15}
                pageSizeOptions={[10, 15, 20, 100]}
                columns={(() => {
                  // Inline editor input — only rendered when `editMode` is on.
                  // Stored values are normalised to integers (Math.round) so the
                  // server-side SnapshotChannelRow / skuData stay numeric.
                  const EditableNumber = ({ value, onCommit, color, testId }) => (
                    <input
                      type="number"
                      defaultValue={Math.round(Number(value) || 0)}
                      onBlur={(e) => onCommit(Math.round(Number(e.target.value) || 0))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                      }}
                      className={`w-20 text-right tabular-nums px-1 py-0.5 border border-[#FB923C] rounded bg-[#FFF7ED] text-[12px] focus:outline-none focus:ring-1 focus:ring-[#FB923C] ${color || ''}`}
                      data-testid={testId}
                    />
                  );
                  const cols = [
                    { key: 'ASIN', header: 'ASIN', width: '110px', nowrap: true, sortable: true,
                      render: (r) => r.ASIN || '-' },
                    { key: 'SKU',  header: 'SKU', sortable: true,
                      render: (r) => <span className="font-medium text-[#0F0F0F]">{r.SKU || '-'}</span> },
                    { key: 'BRAND', header: 'Brand', sortable: true, width: '140px',
                      render: (r) => r.BRAND || <span className="text-[#64748B]">—</span> },
                    {
                      key: 'currentStock',
                      header: 'Current Stock',
                      align: 'right',
                      sortable: true,
                      sortAccessor: (r) => parseNumber(r['Total Available'] || r.Total_Available),
                      render: (r) => (
                        <span className="tabular-nums text-[#334155]">
                          {formatIndianNumber(parseNumber(r['Total Available'] || r.Total_Available))}
                        </span>
                      ),
                    },
                    {
                      key: 'totalDRR',
                      header: 'DRR',
                      align: 'right',
                      sortable: true,
                      sortAccessor: (r) => parseNumber(r['Total DRR'] || r.Total_DRR),
                      render: (r) => (
                        <span className="tabular-nums text-[#334155]">
                          {formatIndianNumber(parseNumber(r['Total DRR'] || r.Total_DRR))}
                        </span>
                      ),
                    },
                    {
                      key: 'allocatableStock',
                      header: 'Allocatable',
                      align: 'right',
                      sortable: true,
                      sortAccessor: (r) => r.allocatableStock,
                      render: (r) => {
                        if (editMode) {
                          const skuKey = `${r.ASIN || 'no-asin'}-${r.SKU || ''}`;
                          return (
                            <EditableNumber
                              value={r.allocatableStock}
                              onCommit={(v) => updateEditedSku(skuKey, 'allocatableStock', v)}
                              color="text-[#0F0F0F]"
                              testId={`edit-allocatable-${skuKey}`}
                            />
                          );
                        }
                        return (
                          <span
                            title={
                              r.isStockDeficit
                                ? `Deficit: raw allocatable ${formatIndianNumber(r.rawAllocatable)} (Base − Safety − B2C). Shown as 0.`
                                : r.isZeroDRR
                                ? 'Zero DRR — no allocation needed.'
                                : 'Base − Safety − B2C'
                            }
                            className={`tabular-nums font-medium ${
                              r.isStockDeficit ? 'text-[#B91C1C]' : r.isZeroDRR ? 'text-[#64748B]' : 'text-[#2EB4AC]'
                            }`}
                          >
                            {formatIndianNumber(r.allocatableStock)}
                            {r.isStockDeficit && <span className="ml-1 text-[10px]">⚠</span>}
                          </span>
                        );
                      },
                    },
                  ];
                  if (effectiveChannels.includes('B2B DRR')) {
                    cols.push({
                      key: 'offlineFinalRequirement',
                      header: 'Offline',
                      align: 'right',
                      sortable: true,
                      sortAccessor: (r) => r.offlineFinalRequirement,
                      render: (r) => {
                        if (editMode) {
                          const skuKey = `${r.ASIN || 'no-asin'}-${r.SKU || ''}`;
                          return (
                            <EditableNumber
                              value={r.offlineFinalRequirement || 0}
                              onCommit={(v) => updateEditedSku(skuKey, 'offlineFinalRequirement', v)}
                              color="text-[#C2410C]"
                              testId={`edit-offline-${skuKey}`}
                            />
                          );
                        }
                        return <span className="tabular-nums font-medium text-[#C2410C]">{formatIndianNumber(r.offlineFinalRequirement || 0)}</span>;
                      },
                    });
                  }
                  CHANNEL_OPTIONS
                    .filter(opt => opt.value !== 'B2B DRR' && effectiveChannels.includes(opt.value))
                    .forEach(opt => {
                      cols.push({
                        key: opt.value,
                        header: opt.label,
                        align: 'right',
                        sortable: true,
                        sortAccessor: (r) => r.allocations?.[opt.value] || 0,
                        render: (r) => {
                          if (editMode) {
                            const skuKey = `${r.ASIN || 'no-asin'}-${r.SKU || ''}`;
                            return (
                              <EditableNumber
                                value={r.allocations?.[opt.value] || 0}
                                onCommit={(v) => updateEditedSku(skuKey, `alloc:${opt.value}`, v)}
                                testId={`edit-${opt.label.toLowerCase().replace(/\s+/g, '-')}-${skuKey}`}
                              />
                            );
                          }
                          return <span className="tabular-nums">{formatIndianNumber(r.allocations?.[opt.value] || 0)}</span>;
                        },
                      });
                    });
                  return cols;
                })()}
                renderTopRow={(rows) => {
                  const totalCurrent = rows.reduce((s, r) => s + parseNumber(r['Total Available'] || r.Total_Available), 0);
                  const totalDRR = rows.reduce((s, r) => s + parseNumber(r['Total DRR'] || r.Total_DRR), 0);
                  const totalAllocatable = rows.reduce((s, r) => s + (r.allocatableStock || 0), 0);
                  const totalOffline = rows.reduce((s, r) => s + (r.offlineFinalRequirement || 0), 0);
                  const channelTotals = {};
                  CHANNEL_OPTIONS
                    .filter(opt => opt.value !== 'B2B DRR' && effectiveChannels.includes(opt.value))
                    .forEach(opt => {
                      channelTotals[opt.value] = rows.reduce((s, r) => s + (r.allocations?.[opt.value] || 0), 0);
                    });
                  return (
                    <>
                      <td className="px-3 py-2 text-[13px] font-semibold text-white" style={{ whiteSpace: 'nowrap' }}>Total</td>
                      <td className="px-3 py-2 text-[13px] text-white/50">—</td>
                      <td className="px-3 py-2 text-[13px] text-white/50">—</td>
                      <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-current-stock">
                        {formatIndianNumber(totalCurrent)}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-drr">
                        {formatIndianNumber(totalDRR)}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-white" data-testid="totals-allocatable">
                        {formatIndianNumber(totalAllocatable)}
                      </td>
                      {effectiveChannels.includes('B2B DRR') && (
                        <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-[#FDBA74]" data-testid="totals-offline">
                          {formatIndianNumber(totalOffline)}
                        </td>
                      )}
                      {CHANNEL_OPTIONS
                        .filter(opt => opt.value !== 'B2B DRR' && effectiveChannels.includes(opt.value))
                        .map(opt => (
                          <td
                            key={opt.value}
                            className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-[#0F0F0F]"
                            data-testid={`totals-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {formatIndianNumber(channelTotals[opt.value])}
                          </td>
                        ))}
                    </>
                  );
                }}
                rows={displaySkuRows.map((row, idx) => ({ ...row, __key: `${row.ASIN || 'no-asin'}-${row.SKU || idx}` }))}
              />
            </section>
          </>
        )}
      </main>

      {/* Editor unlock dialog — collects the editor password, validates against
          the backend, then persists for the session. */}
      <Dialog open={unlockDialogOpen} onOpenChange={(o) => { if (!o) { setUnlockDialogOpen(false); setUnlockPwInput(''); } }}>
        <DialogContent className="max-w-sm" data-testid="editor-unlock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LockKeyhole size={18} className="text-primary" />
              Unlock Edit Mode
            </DialogTitle>
            <DialogDescription className="text-xs">
              Enter the editor password to override saved allocation values for this snapshot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Editor Password</Label>
            <Input
              type="password"
              autoFocus
              value={unlockPwInput}
              onChange={(e) => setUnlockPwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
              placeholder="Enter password"
              data-testid="editor-unlock-input"
              className="h-9"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setUnlockDialogOpen(false); setUnlockPwInput(''); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleUnlock} disabled={unlocking || !unlockPwInput.trim()} data-testid="editor-unlock-submit">
              {unlocking ? 'Verifying…' : 'Unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
